import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getAuthTermsArticles, postAuthTermsAgreeBulk } from '@entities/auth'
import { ConfirmModal } from '@shared/ui'
import { IconCheckSmall, IconChevronDownTiny, IconClose } from '@shared/ui/icons'

export interface RiskAgreementTerm {
  id: number
  order: number
  title: string
  content: string
  required: boolean
}

interface RiskAgreementModalProps {
  isOpen: boolean
  needAgree: string[]
  onAgreed: () => void
  onCancel: () => void
}

export const RiskAgreementModal = ({
  isOpen,
  needAgree,
  onAgreed,
  onCancel,
}: RiskAgreementModalProps) => {
  const [checkedIds, setCheckedIds] = useState<Set<number>>(() => new Set())
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set())
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)

  const termsTypes = useMemo(() => {
    const values = needAgree.filter(Boolean)
    return values.length > 0 ? values : ['exemption']
  }, [needAgree])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['auth', 'terms', 'articles', termsTypes],
    queryFn: () => getAuthTermsArticles(termsTypes),
    enabled: isOpen,
  })

  const terms = useMemo(() => mapTermsArticlesResponse(data), [data])
  const requiredTerms = useMemo(() => terms.filter(term => term.required), [terms])
  const allRequiredChecked = requiredTerms.length > 0 && requiredTerms.every(term => checkedIds.has(term.id))
  const allChecked = terms.length > 0 && terms.every(term => checkedIds.has(term.id))

  useEffect(() => {
    if (!isOpen) return
    setCheckedIds(new Set())
    setExpandedIds(new Set())
  }, [isOpen, termsTypes])

  const { mutate: agreeBulk, isPending } = useMutation({
    mutationFn: () =>
      postAuthTermsAgreeBulk({
        termsIdx: Array.from(checkedIds),
      }),
    onSuccess: data => {
      if (data.code !== undefined && data.code !== 200) return
      setCheckedIds(new Set())
      setExpandedIds(new Set())
      onAgreed()
    },
  })

  if (!isOpen) return null

  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(new Set())
      return
    }
    setCheckedIds(new Set(terms.map(term => term.id)))
  }

  const toggleTerm = (id: number) => {
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleExpanded = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const requestClose = () => {
    setConfirmCloseOpen(true)
  }

  const confirmClose = () => {
    setConfirmCloseOpen(false)
    setCheckedIds(new Set())
    setExpandedIds(new Set())
    onCancel()
  }

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 px-4 py-8"
      >
        <section className="flex h-[min(620px,calc(100vh-64px))] w-full max-w-[460px] flex-col overflow-hidden rounded-[14px] border border-[#444546] bg-[#1b1c1d] shadow-[0px_8px_24px_rgba(0,0,0,0.45)]">
          <header className="relative flex h-[58px] shrink-0 items-center justify-center border-b-4 border-[#32363d]">
            <h2 className="font-pretendard text-[15px] font-semibold text-[#efe9e9]">
              이용약관
            </h2>
            <button
              type="button"
              onClick={requestClose}
              className="absolute right-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center"
              aria-label="닫기"
            >
              <IconClose />
            </button>
          </header>

          <div className="shrink-0 px-5 pb-3 pt-4">
            <h3 className="font-pretendard text-[17px] font-semibold leading-6 text-[#efe9e9]">
              SUPER META 이용약관
            </h3>
            <button
              type="button"
              onClick={toggleAll}
              disabled={terms.length === 0}
              className="mt-3 flex items-center gap-2 self-start disabled:opacity-50"
            >
              <AgreementCheckbox checked={allChecked} />
              <span className="font-pretendard text-[13px] font-semibold text-[#efe9e9]">
                전체 동의
              </span>
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 pr-4 scrollbar-yellow">
            {isLoading && <StatusBox message="약관을 불러오는 중입니다." />}
            {isError && (
              <StatusBox message="약관을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요." />
            )}
            {!isLoading && !isError && terms.length === 0 && (
              <StatusBox message="동의가 필요한 약관이 없습니다." />
            )}

            {terms.map(term => {
              const checked = checkedIds.has(term.id)
              const expanded = expandedIds.has(term.id)

              return (
                <article
                  key={term.id}
                  className="mb-2 border border-[#444546] bg-[#202121] px-4 py-3"
                >
                  <div className="flex min-h-[30px] items-start gap-3">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(term.id)}
                      aria-expanded={expanded}
                      className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    >
                      <span className="min-w-0 font-pretendard text-[14px] font-semibold leading-5 text-[#efe9e9]">
                        {term.title}
                      </span>
                      <IconChevronDownTiny
                        className={`mt-[4px] shrink-0 transition-transform ${
                          expanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    <button
                      type="button"
                      onClick={() => toggleTerm(term.id)}
                      className="flex shrink-0 items-center gap-2 pt-[1px]"
                    >
                      <AgreementCheckbox checked={checked} />
                      <span className="font-pretendard text-[14px] font-semibold text-[#efe9e9]">
                        동의합니다.
                      </span>
                    </button>
                  </div>

                  {expanded && term.content && (
                    <p className="mt-3 whitespace-pre-line border-t border-[#32363d] pt-3 font-pretendard text-[12px] leading-5 text-[#b8b4b6]">
                      {term.content}
                    </p>
                  )}
                </article>
              )
            })}
          </div>

          <div className="flex shrink-0 gap-3 px-5 pb-5 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="flex h-11 flex-1 items-center justify-center rounded-full border border-[#efe9e9] font-pretendard text-[14px] font-semibold text-[#efe9e9]"
            >
              취소
            </button>
            <button
              type="button"
              onClick={() => agreeBulk()}
              disabled={!allRequiredChecked || isPending}
              className={`flex h-11 flex-1 items-center justify-center rounded-full font-pretendard text-[14px] font-semibold ${
                allRequiredChecked && !isPending
                  ? 'bg-[#efe9e9] text-[#1b1c1d]'
                  : 'bg-[#b8b4b6] text-[#32363d]'
              }`}
            >
              {isPending ? '처리 중...' : '약관 동의하기'}
            </button>
          </div>
        </section>
      </div>

      <ConfirmModal
        isOpen={confirmCloseOpen}
        title="동의하지 않으시면 서비스를 이용하실 수 없습니다. 창을 닫으시겠습니까?"
        confirmLabel="닫기"
        cancelLabel="취소"
        onConfirm={confirmClose}
        onCancel={() => setConfirmCloseOpen(false)}
      />
    </>
  )
}

const AgreementCheckbox = ({ checked }: { checked: boolean }) => (
  <span
    aria-hidden="true"
    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] border ${
      checked
        ? 'border-[#efe9e9] bg-[#efe9e9]'
        : 'border-[#444546] bg-transparent'
    }`}
  >
    {checked && <IconCheckSmall />}
  </span>
)

const StatusBox = ({ message }: { message: string }) => (
  <div className="mx-5 mt-5 border border-[#444546] bg-[#202121] px-4 py-6 text-center font-pretendard text-[13px] text-[#b8b4b6]">
    {message}
  </div>
)

const isRequiredTerm = (value: boolean | number | string | undefined) => {
  if (value === undefined) return true
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  return value === '1' || value.toUpperCase() === 'Y' || value.toLowerCase() === 'true'
}

type TermsArticlesNestedPayload = {
  terms?: TermsArticle[]
  articles?: TermsArticle[]
  data?: TermsArticle[] | TermsArticlesNestedPayload
  result?: TermsArticle[] | TermsArticlesNestedPayload
  payload?: TermsArticle[] | TermsArticlesNestedPayload
  list?: TermsArticle[]
  items?: TermsArticle[]
  rows?: TermsArticle[]
}

const mapTermsArticlesResponse = (data: TermsArticlesResponse | undefined): RiskAgreementTerm[] => {
  if (!data || (data.code !== undefined && data.code !== 200)) return []

  const list = getTermsArticleList(data)
  return list
    .map((term, index) => {
      const order = term.order ?? term.sortOrder ?? term.sort ?? term.seq ?? term.no ?? index + 1
      return {
        id: term.id ?? term.idx ?? term.termIdx ?? term.termsIdx ?? term.articleId ?? term.articleIdx ?? order,
        order,
        title: term.title ?? term.subject ?? term.name ?? `제 ${order}조`,
        content: term.content ?? term.contents ?? term.body ?? '',
        required: isRequiredTerm(term.required ?? term.isRequired ?? term.requiredYn),
      }
    })
    .sort((a, b) => a.order - b.order)
}

const getTermsArticleList = (data: TermsArticlesResponse): TermsArticle[] => {
  const payload = data as unknown as TermsArticlesNestedPayload
  return pickTermsArticleList(payload) ?? []
}

const pickTermsArticleList = (
  payload: TermsArticlesNestedPayload | TermsArticle[] | undefined
): TermsArticle[] | undefined => {
  if (!payload) return undefined
  if (Array.isArray(payload)) return payload

  if (payload.terms) return payload.terms
  if (payload.articles) return payload.articles
  if (payload.list) return payload.list
  if (payload.items) return payload.items
  if (payload.rows) return payload.rows

  return (
    pickTermsArticleList(payload.data) ??
    pickTermsArticleList(payload.result) ??
    pickTermsArticleList(payload.payload)
  )
}
